import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated } from 'react-native';
import { Colors } from '@/constants/colors';

interface SkeletonCardProps {
  height?: number;
  lines?: number;
  showHeader?: boolean;
}

export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  height,
  lines = 3,
  showHeader = true,
}) => {
  const shimmerAnimation = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnimation, {
          toValue: 1,
          duration: 1500,
          useNativeDriver: true,
        }),
        Animated.timing(shimmerAnimation, {
          toValue: 0,
          duration: 1500,
          useNativeDriver: true,
        }),
      ])
    );
    animation.start();
    return () => animation.stop();
  }, [shimmerAnimation]);

  const opacity = shimmerAnimation.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 0.7],
  });

  return (
    <View style={[styles.card, height ? { height } : undefined]}>
      {showHeader && (
        <View style={styles.header}>
          <Animated.View style={[styles.iconSkeleton, { opacity }]} />
          <Animated.View style={[styles.titleSkeleton, { opacity }]} />
        </View>
      )}
      <View style={styles.content}>
        {Array.from({ length: lines }).map((_, index) => (
          <Animated.View
            key={index}
            style={[styles.lineSkeleton, { opacity }, index === lines - 1 && { width: '70%' }]}
          />
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  iconSkeleton: {
    width: 20,
    height: 20,
    borderRadius: 4,
    backgroundColor: Colors.border.default,
  },
  titleSkeleton: {
    width: 140,
    height: 16,
    borderRadius: 4,
    backgroundColor: Colors.border.default,
  },
  content: {
    gap: 8,
  },
  lineSkeleton: {
    width: '100%',
    height: 14,
    borderRadius: 4,
    backgroundColor: Colors.border.default,
  },
});
